import { useEffect, useState } from 'react'
import { Icon } from './components/Icon'
import { Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { api, ApiError, isPreviewing, onSessionExpired, stopPreview } from './api'
import type { CurrentUser } from './types'
import { Layout } from './components/Layout'
import { Login } from './pages/Login'
import { Dashboard } from './pages/Dashboard'
import { Report } from './pages/Report'
import { DataHealth } from './pages/DataHealth'
import { IngestStatus } from './pages/IngestStatus'
import { AdminAccess } from './pages/AdminAccess'
import { AdminUsers } from './pages/AdminUsers'
import { AdminLog } from './pages/AdminLog'
import { AdminRules } from './pages/AdminRules'
import { AdminTokens } from './pages/AdminTokens'
import { AdminFinance } from './pages/AdminFinance'
import { Upload } from './pages/Upload'
import { ReportsCatalog } from './pages/ReportsCatalog'
import { AdminUserAccess } from './pages/AdminUserAccess'
import { FinanceBlock, FINANCE_SECTIONS, useCurrentSection, visibleSections } from './finance/FinanceBlock'
import { Empty, Loading } from './components/ui'
import { Card } from './components/ui'
import { SalesBlock, useSalesSection } from './insights/SalesBlock'
import { StockBlock, useStockSection } from './insights/StockBlock'
import { SALES_SECTIONS, STOCK_SECTIONS } from './insights/sections'
import { withViewTransition } from './finance/motion'

/**
 * Ненайденный адрес показывается явно, а не молча переносит на главную:
 * иначе неверная ссылка выглядит как «отчёт исчез».
 */
function NotFound({ path }: { path: string }) {
  return (
    <Card>
      <Empty
        icon="search"
        title="Такой страницы нет"
        hint={
          <>
            Адрес <code>{path}</code> не существует или отчёт вам недоступен.{' '}
            <Link to="/">Вернуться на главную</Link>
          </>
        }
      />
    </Card>
  )
}

export function App() {
  const [user, setUser] = useState<CurrentUser | null>(null)
  const [checking, setChecking] = useState(true)
  const [expired, setExpired] = useState(false)
  const location = useLocation()

  useEffect(
    () =>
      onSessionExpired(() =>
        // Приложение гаснет, карточка входа поднимается — не обрыв на полуслове.
        withViewTransition(() => {
          setUser(null)
          setExpired(true)
        }, 'logout'),
      ),
    [],
  )

  useEffect(() => {
    api
      .me()
      .catch((caught: unknown) => {
        // Просмотр стал невозможен (сотрудника заблокировали, вошёл не
        // администратор): выходим из просмотра и спрашиваем ещё раз от себя,
        // а не выбрасываем на вход с застрявшим as_user.
        if (!isPreviewing()) throw caught
        stopPreview()
        return api.me()
      })
      .then((response) => setUser(response.user))
      .catch(() => setUser(null))
      .finally(() => setChecking(false))
  }, [])

  if (checking) {
    return (
      <div style={{ padding: 40, maxWidth: 640, margin: '0 auto' }}>
        <Loading rows={4} />
      </div>
    )
  }

  if (!user) {
    return (
      <Routes>
        <Route
          path="*"
          element={
            <Login
              notice={expired ? 'Сессия закончилась — войдите снова. Страница откроется там же, где вы были.' : null}
              onLogin={(next) =>
                // Карточка входа уходит, каркас приложения проявляется со скелетоном раздела.
                withViewTransition(() => {
                  setExpired(false)
                  setUser(next)
                }, 'login')
              }
            />
          }
        />
      </Routes>
    )
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route
        path="/"
        element={
          // Подрядчику 1С отчёты не выдают, и обзор для него пустой.
          // Его работа — загрузка данных, с неё и открывается система.
          user.reports.length === 0 && user.can_monitor_ingest ? (
            <Navigate to="/ingest" replace />
          ) : visibleSections(user).length > 0 ? (
            <FinanceRoute user={user} onLogout={() => setUser(null)} />
          ) : (
          <Layout
            user={user}
            onLogout={() => setUser(null)}
            title="Обзор"
            subtitle={`${user.full_name}${user.roles.length > 0 ? ` · ${user.roles.map((role) => role.title).join(', ')}` : ''}`}
          >
            <Dashboard user={user} />
          </Layout>
          )
        }
      />
      <Route path="/finance" element={<Navigate to="/" replace />} />
      <Route path="/finance/:section" element={<FinanceRoute user={user} onLogout={() => setUser(null)} />} />
      <Route
        path="/summary"
        element={
          <Layout user={user} onLogout={() => setUser(null)} title="Краткая сводка" subtitle="Плитки за 12 месяцев, актуальность данных и последние загрузки">
            <Dashboard user={user} />
          </Layout>
        }
      />
      {/* Продажи и склад — самостоятельные разделы; адреса и пункты меню в insights/sections.ts. */}
      <Route path="/sales" element={<Navigate to="/sales/managers" replace />} />
      <Route path="/sales/:section" element={<SalesRoute user={user} onLogout={() => setUser(null)} />} />
      <Route path="/stock" element={<Navigate to="/stock/stock" replace />} />
      <Route path="/stock/:section" element={<StockRoute user={user} onLogout={() => setUser(null)} />} />
      <Route
        path="/reports"
        element={
          <Layout
            user={user}
            onLogout={() => setUser(null)}
            title="Дополнительные отчёты"
            subtitle="Таблицы с ручной группировкой и выгрузкой в Excel — всё, что вам выдано"
          >
            <ReportsCatalog user={user} />
          </Layout>
        }
      />
      <Route path="/reports/:code" element={<ReportRoute user={user} onLogout={() => setUser(null)} />} />
      {/* Старый адрес: у кого-то мог остаться в закладках. */}
      <Route path="/ingest" element={<Navigate to="/uploads" replace />} />
      <Route
        path="/uploads"
        element={
          <Layout
            user={user}
            onLogout={() => setUser(null)}
            title="Загрузка данных"
            subtitle="Что прислано, что разобралось, что нужно исправить"
          >
            {user.is_admin || user.can_monitor_ingest ? (
              <IngestStatus user={user} />
            ) : (
              // Кабинет загрузки — отдельное право. В просмотре «как пользователь»
              // здесь иначе открылись бы данные самого администратора.
              <Card>
                <Empty
                  icon="lock"
                  title={user.preview ? `У сотрудника «${user.full_name}» нет кабинета загрузки` : 'Кабинет загрузки вам не выдан'}
                  hint={
                    user.preview
                      ? 'Этот раздел он не видит: кабинет загрузки выдаётся отдельно от отчётов, на экране прав сотрудника.'
                      : 'Состояние обмена с 1С видят администратор и ответственный за загрузку. Свежесть данных указана в каждом отчёте.'
                  }
                />
              </Card>
            )}
          </Layout>
        }
      />
      <Route
        path="/data"
        element={
          !(user.is_admin || user.can_monitor_ingest) ? (
            <Navigate to="/" replace />
          ) : (
          <Layout
            user={user}
            onLogout={() => setUser(null)}
            title="Загрузки и сверка"
            subtitle="Состояние данных: когда приехали, что сломалось, сошлись ли итоги"
          >
            <DataHealth />
          </Layout>
          )
        }
      />
      <Route
        path="/upload"
        element={
          <AdminRoute
            user={user}
            onLogout={() => setUser(null)}
            title="Загрузка Excel"
            subtitle="Ручные выгрузки клиента, пока не готова интеграция с 1С"
          >
            <Upload />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/access"
        element={
          <AdminRoute user={user} onLogout={() => setUser(null)} title="Матрица доступа" subtitle="Какие отчёты выданы каждой роли; по названию роли — её разделы, показатели и срез">
            <AdminAccess />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <AdminRoute
            user={user}
            onLogout={() => setUser(null)}
            title="Пользователи"
            subtitle="Роли дают отчёты и разделы меню, личные правила уточняют срез и скрывают лишнее"
          >
            <AdminUsers />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/users/:id"
        element={
          <AdminRoute
            user={user}
            onLogout={() => setUser(null)}
            title="Права пользователя"
            subtitle="Что человек видит в итоге, откуда это взялось, и правка всего в одном месте"
          >
            <AdminUserAccess />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/rules"
        element={
          <AdminRoute
            user={user}
            onLogout={() => setUser(null)}
            title="Правила филиалов"
            subtitle="Филиала как справочника в 1С нет: он выводится из наименований подразделений и складов"
          >
            <AdminRules />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/finance"
        element={
          <AdminRoute
            user={user}
            onLogout={() => setUser(null)}
            title="Финансовая разметка"
            subtitle="Свои организации, разделы статей ДДС и статьи расходов — того, чего нет в 1С"
          >
            <AdminFinance />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/tokens"
        element={
          <AdminRoute
            user={user}
            onLogout={() => setUser(null)}
            title="Токены 1С"
            subtitle="Кому выдано право присылать данные и когда этим правом пользовались"
          >
            <AdminTokens />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/log"
        element={
          <AdminRoute
            user={user}
            onLogout={() => setUser(null)}
            title="Журналы"
            subtitle="Кто что смотрел с какими фактически применёнными фильтрами — и кто что менял в правах"
          >
            <AdminLog />
          </AdminRoute>
        }
      />
      <Route
        path="*"
        element={
          <Layout user={user} onLogout={() => setUser(null)} title="Страница не найдена">
            <NotFound path={location.pathname} />
          </Layout>
        }
      />
    </Routes>
  )
}

/**
 * Неизвестный раздел внутри блока (`/finance/zzz`) — «Такой страницы нет»,
 * а не молчаливый обзор: иначе опечатка в ссылке выглядит как рабочий адрес.
 */
function UnknownSection({ user, onLogout }: { user: CurrentUser; onLogout: () => void }) {
  const location = useLocation()
  return (
    <Layout user={user} onLogout={onLogout} title="Страница не найдена">
      <NotFound path={location.pathname} />
    </Layout>
  )
}

function FinanceRoute({ user, onLogout }: { user: CurrentUser; onLogout: () => void }) {
  const code = useCurrentSection()
  const { section: requested } = useParams()
  if (requested && !FINANCE_SECTIONS.some((item) => item.code === requested)) return <UnknownSection user={user} onLogout={onLogout} />
  const section = FINANCE_SECTIONS.find((item) => item.code === code) ?? FINANCE_SECTIONS[0]!
  return (
    <Layout
      user={user}
      onLogout={onLogout}
      title={<h1 className="fin-title">{section.title}</h1>}
      subtitle={section.subtitle}
      documentTitle={section.title}
    >
      <FinanceBlock user={user} />
    </Layout>
  )
}

function SalesRoute({ user, onLogout }: { user: CurrentUser; onLogout: () => void }) {
  const code = useSalesSection()
  const { section: requested } = useParams()
  if (requested && !SALES_SECTIONS.some((item) => item.code === requested)) return <UnknownSection user={user} onLogout={onLogout} />
  const section = SALES_SECTIONS.find((item) => item.code === code) ?? SALES_SECTIONS[0]!
  return (
    <Layout user={user} onLogout={onLogout} title={<h1 className="fin-title">{section.title}</h1>} subtitle={section.subtitle} documentTitle={section.title}>
      <SalesBlock user={user} />
    </Layout>
  )
}

function StockRoute({ user, onLogout }: { user: CurrentUser; onLogout: () => void }) {
  const code = useStockSection()
  const { section: requested } = useParams()
  if (requested && !STOCK_SECTIONS.some((item) => item.code === requested)) return <UnknownSection user={user} onLogout={onLogout} />
  const section = STOCK_SECTIONS.find((item) => item.code === code) ?? STOCK_SECTIONS[0]!
  return (
    <Layout user={user} onLogout={onLogout} title={<h1 className="fin-title">{section.title}</h1>} subtitle={section.subtitle} documentTitle={section.title}>
      <StockBlock user={user} />
    </Layout>
  )
}

function ReportRoute({ user, onLogout }: { user: CurrentUser; onLogout: () => void }) {
  const { code = '' } = useParams()
  const descriptor = user.reports.find((report) => report.code === code)

  return (
    <Layout
      user={user}
      onLogout={onLogout}
      title={descriptor?.title ?? 'Отчёт'}
      documentTitle={descriptor?.title ?? 'Отчёт'}
      subtitle={
        <>
          <Link to="/reports" className="back-link"><Icon name="arrow-left" size={14} /> Дополнительные отчёты</Link>
          {descriptor?.description ? ` · ${descriptor.description}` : ''}
        </>
      }
    >
      <Report user={user} />
    </Layout>
  )
}

function AdminRoute({
  user,
  onLogout,
  title,
  subtitle,
  children,
}: {
  user: CurrentUser
  onLogout: () => void
  title: string
  subtitle: string
  children: React.ReactNode
}) {
  if (!user.is_admin) return <Navigate to="/" replace />
  return (
    <Layout user={user} onLogout={onLogout} title={title} subtitle={subtitle}>
      {children}
    </Layout>
  )
}

export { ApiError }
